import React, { useState, useEffect, useRef, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Trash2, Edit2, Play, Music, CheckCircle, AlertCircle, ListMusic, Volume2, Lock, Star, Crown, Loader2 } from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useFullscreen } from '@/contexts/FullscreenContext';
import UnifiedAudioControls from '@/components/UnifiedAudioControls';
import { useTherapyTimer } from '@/hooks/useTherapyTimer';
import therapyTimer from '@/services/therapyTimer';
import CoachTherapyBanner from '@/components/CoachTherapyBanner';
import CircularTherapyTimer from '@/components/CircularTherapyTimer';
import AudioTherapyToggles from '@/components/AudioTherapyToggles';
import audioService from '@/services/audioService'; // For equalizer management
import soundscapeService from '@/services/soundscapeService'; // For crossfade playback
import { CANONICAL_THEMES } from '@/config/themes';
import { AUDIO_BANDS_COUNT } from '@/config/audioProfile';
import { UnifiedPlayer } from '@/components/player';
import AudioProfileModal from '@/components/AudioProfileModal';
import useLanguage from '@/hooks/useLanguage';
import { extToM4a } from '@/utils/audioUtils';
// BUILD 58: Use getBaseUrl for iOS compatibility
import { buildAudioUrl, reencodeAudioPath, getBaseUrl } from '@/services/httpClient';
import { quantizeDb } from '@/utils/tinnitusDisplay';

const Playlists = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setIsFullscreen } = useFullscreen();
  const { lang: currentLang } = useLanguage(); // V1.1: Reactive language
  const [isPending, startTransition] = useTransition();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recentlyAddedSound, setRecentlyAddedSound] = useState(null);
  
  // V1.1: Soft limit message for Free users
  const [limitMessage, setLimitMessage] = useState(null);
  
  // Premium modal state
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  
  // Check if user is Free
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
  
  // Create playlist modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  
  // Edit playlist state
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showAddSoundsModal, setShowAddSoundsModal] = useState(false);
  const [availableSounds, setAvailableSounds] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('nature');
  
  // State for add sounds modal loading
  const [loadingSounds, setLoadingSounds] = useState(false);
  const [loadingSoundsError, setLoadingSoundsError] = useState(null);
  
  // Delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, playlistId: null, playlistName: '' });
  
  // Audio player state (for full playback via UnifiedPlayer)
  const [playingSound, setPlayingSound] = useState(null); // Currently playing sound object
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerActive, setPlayerActive] = useState(false); // Track if player view is active (for pause state)
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(0);
  
  // Preview state (independent from main player)
  const PREVIEW_DURATION_MS = 10000; // 10 seconds preview
  const previewAudioRef = useRef(null);
  const previewTimeoutRef = useRef(null);
  const [previewingSound, setPreviewingSound] = useState(null); // Sound currently being previewed
  
  // Get volume from localStorage or default to 80%
  const getInitialVolume = () => {
    try {
      const saved = localStorage.getItem('hearen-playlists-volume');
      if (saved !== null) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error loading volume from localStorage:', e);
    }
    return 80; // Default to 80%
  };
  
  // V1 Unified Player states
  const [volume, setVolume] = useState(() => getInitialVolume()); // 80% default
  const [userSettings, setUserSettings] = useState(null);
  
  // Persist volume to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('hearen-playlists-volume', volume.toString());
    } catch (e) {
      console.error('Error saving volume to localStorage:', e);
    }
  }, [volume]);
  
  // Therapy timer hook
  const { timerActive, sessionElapsedSeconds } = useTherapyTimer();
  
  // FIX 1: Sync fullscreen state with player active state
  useEffect(() => {
    setIsFullscreen(playerActive);
    return () => setIsFullscreen(false);
  }, [playerActive, setIsFullscreen]);
  
  // Equalizer state (same as TherapyPlayer)
  const [equalizerSettings, setEqualizerSettings] = useState(null);
  const [equalizerEnabled, setEqualizerEnabled] = useState(false); // OFF by default like TherapyPlayer
  const [notchEnabled, setNotchEnabled] = useState(false); // OFF by default like TherapyPlayer
  const [labProgram, setLabProgram] = useState(null);
  
  // FIX 1: Audio Profile Modal state (same as Radio)
  const [showSpectrumPanel, setShowSpectrumPanel] = useState(false);
  
  // Audio element ref removed — soundscapeService manages its own audio nodes
  
  // V3: Crossfade management refs
  const crossfadeTimerRef = useRef(null);
  const isTransitioningRef = useRef(false);
  
  // V2.2.5: Refs for playlist state (avoids stale closure in onEnded callbacks)
  const currentPlaylistRef = useRef(null);
  const currentPlaylistIndexRef = useRef(0);
  const [loadingPlaylistId, setLoadingPlaylistId] = useState(null); // Loading state for écouter button
  
  // Favorites state
  const [favorites, setFavorites] = useState([]);

  // Use canonical themes from single source of truth with i18n
  // t is an object, not a function - access nested keys via dot notation
  const getLabel = (labelKey, fallback) => {
    if (t && typeof t === 'object') {
      const keys = labelKey.split('.');
      let value = t;
      for (const key of keys) {
        value = value?.[key];
        if (value === undefined) break;
      }
      if (value && typeof value === 'string') return value;
    }
    return fallback;
  };
  
  const categories = CANONICAL_THEMES.map(theme => ({
    id: theme.id,
    name: getLabel(theme.labelKey, theme.name),
    icon: theme.icon,
  }));

  // V1.1: Track if default playlist check has been done (avoid double call in StrictMode)
  const defaultPlaylistCheckedRef = React.useRef(false);
  
  useEffect(() => {
    // V1.1: Ensure default playlist exists, then load playlists
    const initPlaylists = async () => {
      if (!defaultPlaylistCheckedRef.current) {
        defaultPlaylistCheckedRef.current = true;
        try {
          await api.ensureDefaultPlaylist();
        } catch (error) {
          console.log('ℹ️ [Playlists] Default playlist check:', error.message);
        }
      }
      loadPlaylists();
    };
    
    initPlaylists();
    loadFavorites();
    loadEqualizerSettings();
    
    // Listen for language changes and reload playlists
    const handleLanguageChange = (event) => {
      loadPlaylists();
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);
  
  // Load equalizer settings from backend (EXACTLY like TherapyPlayer)
  const loadEqualizerSettings = async () => {
    try {
      // Use same API endpoint as TherapyPlayer
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/equalizer/${user.id}`
      );
      
      if (response.ok) {
        const equalizerData = await response.json();
        console.log('[Playlists] Equalizer data received:', equalizerData);
        
        setEqualizerSettings(equalizerData);
        setLabProgram(equalizerData.lab_program || null);
        
        const hasAudiometric = equalizerData.has_audiometric_compensation;
        const hasNotch = equalizerData.has_inverse_notch;
        
        // Fetch persisted toggle preferences
        let eqOn = hasAudiometric;
        let notchOn = hasNotch;
        try {
          const togglesRes = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/audio-toggles/${user.id}`
          );
          if (togglesRes.ok) {
            const toggles = await togglesRes.json();
            // Use saved preference, but only if the corresponding test exists
            eqOn = hasAudiometric && toggles.equalizer_enabled;
            notchOn = hasNotch && toggles.notch_enabled;
          }
        } catch (e) {
          console.warn('[Playlists] Could not load audio toggles, using defaults');
        }
        
        // CRITICAL: Always load equalizer to establish audio path
        audioService.loadEqualizer(equalizerData, eqOn, notchOn);
        
        setEqualizerEnabled(eqOn);
        setNotchEnabled(notchOn);
      } else {
        console.error('[Playlists] Failed to load equalizer:', response.status);
      }
    } catch (error) {
      console.error('[Playlists] Error loading equalizer settings:', error);
    }
  };
  
  // Save audio toggle preferences to backend
  const saveAudioToggles = async (eqEnabled, nEnabled) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/audio-toggles/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ equalizer_enabled: eqEnabled, notch_enabled: nEnabled })
      });
    } catch (e) {
      console.warn('[Playlists] Could not save audio toggles');
    }
  };

  // Toggle handlers (same logic as TherapyPlayer)
  const handleEqualizerToggle = (newState) => {
    setEqualizerEnabled(newState);
    audioService.loadEqualizer(equalizerSettings, newState, notchEnabled);
    saveAudioToggles(newState, notchEnabled);
  };
  
  const handleNotchToggle = (newState) => {
    setNotchEnabled(newState);
    audioService.loadEqualizer(equalizerSettings, equalizerEnabled, newState);
    saveAudioToggles(equalizerEnabled, newState);
  };
  
  const loadFavorites = async () => {
    try {
      const data = await api.getFavorites();
      setFavorites(data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  // BUILD 58: Load sounds when modal opens or category changes
  useEffect(() => {
    if (showAddSoundsModal) {
      console.log('[PlaylistAdd] useEffect triggered: modal=' + showAddSoundsModal + ' category=' + selectedCategory);
      setLoadingSounds(true); // Prevent "empty" flash before async starts
      loadAvailableSounds();
      loadFavorites(); // V1.1.1: Refresh favorites when modal opens
    }
  }, [showAddSoundsModal, selectedCategory]);
  
  // BUILD 58: Reset error state when category changes
  useEffect(() => {
    setLoadingSoundsError(null);
  }, [selectedCategory]);
  
  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      soundscapeService.stop({ reason: 'cleanup' });
      if (crossfadeTimerRef.current) {
        clearTimeout(crossfadeTimerRef.current);
        crossfadeTimerRef.current = null;
      }
    };
  }, []);

  // Sync UI state with soundscapeService (hardware controls, background events)
  useEffect(() => {
    const unsubscribe = soundscapeService.onStateChange((payload) => {
      switch (payload.state) {
        case 'playing':
          setIsPlaying(true);
          break;
        case 'paused':
          setIsPlaying(false);
          break;
        case 'stopped':
          setIsPlaying(false);
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, []);

  const loadPlaylists = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPlaylists();
      setPlaylists(data);
      
      // Preload first track of first playlist for instant iOS startup
      if (data.length > 0 && data[0].sounds?.length > 0) {
        const firstTrack = buildTrackForService(data[0].sounds[0]);
        // Only trigger if not already cached
        if (!soundscapeService.getPreloadedBuffer(firstTrack)) {
          console.log(`[Playlists] Preloading first track: ${firstTrack.name || firstTrack.file}`);
          soundscapeService.preloadTrack(firstTrack);
        }
      }
    } catch (err) {
      console.error('Error loading playlists:', err);
      const errorMsg = t?.locale === 'fr-FR' 
        ? 'Erreur lors du chargement des playlists'
        : 'Error loading playlists';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // V1.1.1: Extract canonical path from any format (proxy URL, canonical, legacy)
  const getCanonicalPath = (pathStr) => {
    if (!pathStr) return '';
    const decoded = decodeURIComponent(pathStr);
    // Proxy URL: /api/r2/stream/nature/Nature_001.m4a → nature/Nature_001.m4a
    const streamMatch = decoded.match(/\/api\/r2\/stream\/([^/]+)\/(.+)$/);
    if (streamMatch) return `${streamMatch[1]}/${streamMatch[2]}`;
    // Already canonical: nature/Nature_001.m4a
    if (!decoded.startsWith('/') && decoded.includes('/')) return decoded;
    return decoded;
  };

  // Favorites helper functions - V1.1.2: Handle all legacy path formats
  const isFavorite = (soundPath) => {
    const canonical = getCanonicalPath(soundPath);
    return favorites.some(fav => {
      let favPath = getCanonicalPath(fav.sound?.path);
      // Handle old formats without category prefix (e.g. "Nature_001.m4a" instead of "nature/Nature_001.m4a")
      const category = (fav.sound?.category || '').toLowerCase();
      if (category && !favPath.toLowerCase().startsWith(category + '/')) {
        favPath = `${category}/${favPath}`;
      }
      return favPath === canonical;
    });
  };

  const toggleFavorite = async (sound) => {
    try {
      // V1.1.2: Store canonical path for consistency with TherapyPlayer
      const canonicalPath = getCanonicalPath(sound.path);
      const soundRef = {
        path: canonicalPath,
        name: sound.display_name || sound.filename,
        type: 'library',
        category: selectedCategory
      };

      if (isFavorite(sound.path)) {
        // Remove from favorites - handle old formats too
        await api.removeFavorite(canonicalPath);
        const newFavorites = favorites.filter(fav => {
          let favPath = getCanonicalPath(fav.sound?.path);
          const cat = (fav.sound?.category || '').toLowerCase();
          if (cat && !favPath.toLowerCase().startsWith(cat + '/')) {
            favPath = `${cat}/${favPath}`;
          }
          return favPath !== canonicalPath;
        });
        setFavorites(newFavorites);
      } else {
        // Add to favorites
        const result = await api.addFavorite(soundRef);
        const newFav = {
          id: result.id,
          sound: soundRef,
          created_at: new Date().toISOString()
        };
        const newFavorites = [...favorites, newFav];
        setFavorites(newFavorites);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // V1.1: Handle limit reached with soft message
      if (error.type === 'LIMIT_REACHED') {
        setLimitMessage(error.message);
        setTimeout(() => setLimitMessage(null), 5000);
        return;
      }
    }
  };
  
  const loadAvailableSounds = async () => {
    console.log('[PlaylistAdd] category=' + selectedCategory + ' loading=true');
    setLoadingSounds(true);
    setLoadingSoundsError(null);
    
    try {
      const userLang = localStorage.getItem('hearen_lang') || 'fr';
      
      // Map canonical theme IDs to legacy API category names
      const categoryApiMapping = {
        'classical': 'classique',
        'ambient': 'piano_ambiant',
        'chill_lofi': 'chill_lofi',
        'breathing_meditation': 'respiration',
        'nature': 'nature',
        'sleep': 'sommeil',
        'pulse_flow': 'pulse_flow'
      };
      const apiCategory = categoryApiMapping[selectedCategory] || selectedCategory;
      
      // BUILD 58: Use getBaseUrl() for iOS compatibility (same as Radio/Soundscapes)
      const baseUrl = getBaseUrl();
      const url = `${baseUrl}/api/library-sounds/${apiCategory}?lang=${userLang}`;
      
      console.log('[PlaylistAdd] url=' + url + ' category=' + apiCategory);
      
      const response = await fetch(url);
      const status = response.status;
      
      console.log('[PlaylistAdd] status=' + status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      const sounds = data.sounds || [];
      
      console.log('[PlaylistAdd] category=' + selectedCategory + ' count=' + sounds.length);
      
      // Store under canonical category ID (not legacy)
      setAvailableSounds(prev => ({
        ...prev,
        [selectedCategory]: sounds
      }));
      
    } catch (err) {
      console.error('[PlaylistAdd] error=' + err.message);
      setLoadingSoundsError(err.message);
    } finally {
      setLoadingSounds(false);
    }
  };

  // ============================================
  // CROSSFADE HELPERS (V3)
  // ============================================
  
  /** Build track object compatible with soundscapeService._buildAudioUrl */
  const buildTrackForService = (sound) => ({
    ...sound,
    file: sound.path,
    name: sound.display_name || sound.name,
    category: sound.category || selectedCategory
  });
  
  /** Get next track info from playlist (loops back to start) */
  const getNextTrackInfo = () => {
    const playlist = currentPlaylistRef.current;
    const idx = currentPlaylistIndexRef.current;
    if (!playlist || !playlist.sounds || playlist.sounds.length === 0) return null;
    const nextIndex = (idx + 1) % playlist.sounds.length;
    return { sound: playlist.sounds[nextIndex], index: nextIndex };
  };
  
  /** Preload next track via soundscapeService for gapless crossfade */
  const preloadNextTrack = () => {
    const next = getNextTrackInfo();
    if (!next) return;
    soundscapeService.preloadTrack(buildTrackForService(next.sound));
    console.log(`[Playlist] Preloading next: "${next.sound.name || next.sound.display_name}"`);
  };
  
  /** Schedule crossfade 3.5s before current track ends */
  const scheduleCrossfade = () => {
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }
    
    setTimeout(() => {
      const duration = soundscapeService.getCurrentTrackDuration();
      if (!duration || duration < 8) return;
      
      const position = soundscapeService.getCurrentTrackPosition();
      const remaining = duration - position;
      const crossfadeDuration = 3.5;
      
      if (remaining < crossfadeDuration + 1) return;
      
      const delayMs = Math.max(0, (remaining - crossfadeDuration) * 1000);
      
      crossfadeTimerRef.current = setTimeout(() => {
        performCrossfade();
      }, delayMs);
      
      console.log(`[Playlist Crossfade] Scheduled in ${(delayMs / 1000).toFixed(1)}s (track ${duration.toFixed(0)}s, remaining ${remaining.toFixed(1)}s)`);
    }, 500);
  };
  
  /** Execute crossfade to next track */
  const performCrossfade = async () => {
    if (isTransitioningRef.current) return;
    isTransitioningRef.current = true;
    
    const next = getNextTrackInfo();
    if (!next) {
      isTransitioningRef.current = false;
      return;
    }
    
    currentPlaylistIndexRef.current = next.index;
    
    const result = await soundscapeService.crossfadeTo(buildTrackForService(next.sound), {
      volume: volume / 100,
      onEnded: handleTrackEnded
    });
    
    if (result.success) {
      console.log(`[Playlist Crossfade] Transition to "${next.sound.name || next.sound.display_name}"`);
      setPlayingSound(next.sound);
      setCurrentPlaylistIndex(next.index);
      preloadNextTrack();
      scheduleCrossfade();
    } else {
      console.warn('[Playlist Crossfade] Failed:', result.reason);
    }
    
    setTimeout(() => { isTransitioningRef.current = false; }, 100);
  };
  
  /** Fallback: track ended naturally without scheduled crossfade */
  const handleTrackEnded = () => {
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }
    
    if (isTransitioningRef.current) return;
    
    const next = getNextTrackInfo();
    if (!next) return;
    
    console.log(`[Playlist] Track ended naturally, starting next: "${next.sound.name || next.sound.display_name}"`);
    currentPlaylistIndexRef.current = next.index;
    startPlayback(next.sound, currentPlaylistRef.current, next.index);
  };
  
  // ============================================
  // UNIFIED AUDIO CONTROLS HANDLERS
  // ============================================
  
  /**
   * Start playing a sound or playlist via soundscapeService (with crossfade support)
   */
  const startPlayback = async (sound, playlist = null, index = 0) => {
    try {
      const diag = window.__playlistDiag;
      if (diag) diag.steps.push({ step: '3_startPlayback_enter', ts: performance.now() });
      
      stopPreview();
      
      if (playlist) {
        currentPlaylistRef.current = playlist;
        currentPlaylistIndexRef.current = index;
      }
      
      const trackForService = buildTrackForService(sound);
      if (diag) diag.steps.push({ step: '4_track_normalized', ts: performance.now(), file: trackForService.file });
      
      const result = await soundscapeService.play(trackForService, {
        volume: volume / 100 || 0.80,
        loop: false,
        onEnded: handleTrackEnded
      });
      
      // DIAGNOSTIC: log full timing breakdown
      if (diag) {
        diag.steps.push({ step: '11_play_returned', ts: performance.now(), success: result.success });
        const t0 = diag.t0;
        console.log(`%c[DIAG] Playlist startup timing (total: ${(performance.now() - t0).toFixed(0)}ms)`, 'color: #ff6600; font-weight: bold; font-size: 14px');
        let prev = t0;
        for (const s of diag.steps) {
          const fromStart = (s.ts - t0).toFixed(0);
          const delta = (s.ts - prev).toFixed(0);
          console.log(`  [+${fromStart}ms] (delta ${delta}ms) ${s.step}${s.file ? ' → ' + s.file : ''}${s.track ? ' → ' + s.track : ''}${s.success !== undefined ? ' success=' + s.success : ''}`);
          prev = s.ts;
        }
        // Also log soundscapeService internal metrics
        const metrics = soundscapeService.getLastTransitionMetric?.() || soundscapeService._lastMetric;
        if (metrics) {
          console.log(`  [SoundscapeService metrics] fetchMs=${metrics.fetchMs} decodeMs=${metrics.decodeMs} startGapMs=${metrics.startGapMs} fromCache=${metrics.fromCache}`);
        }
        window.__playlistDiag = null;
      }
      
      if (!result.success) {
        console.error('[Playlists] soundscapeService.play() failed:', result.reason);
        setLoadingPlaylistId(null);
        return;
      }
      
      // CRITICAL: Set volume (soundscapeService manages its own gain nodes)
      soundscapeService.setVolume(volume / 100 || 0.80);
      
      setPlayingSound(sound);
      setIsPlaying(true);
      setPlayerActive(true);
      setLoadingPlaylistId(null);
      
      // Register media session handlers for hardware controls (AirPods, etc.)
      soundscapeService.setMediaSessionHandlers({
        onNext: handleNext,
        onStop: handleStop
      });
      
      if (playlist) {
        setCurrentPlaylist(playlist);
        setCurrentPlaylistIndex(index);
        
        setTimeout(() => {
          preloadNextTrack();
          scheduleCrossfade();
        }, 500);
      }
      
      if (!timerActive) {
        therapyTimer.startActivity({
          type: 'playlist',
          label: playlist ? playlist.name : sound.name,
          category: sound.category || 'music'
        });
      } else {
        therapyTimer.resume();
      }
      
    } catch (error) {
      console.error('[Playlists] Error in startPlayback:', error);
      setLoadingPlaylistId(null);
    }
  };
  
  /**
   * STOP handler - Ends session, closes player
   */
  const handleStop = async () => {
    try {
      await therapyTimer.stop();
      
      if (crossfadeTimerRef.current) {
        clearTimeout(crossfadeTimerRef.current);
        crossfadeTimerRef.current = null;
      }
      
      soundscapeService.stop({ reason: 'user' });
      
      setIsPlaying(false);
      setPlayingSound(null);
      setCurrentPlaylist(null);
      setCurrentPlaylistIndex(0);
      setPlayerActive(false);
      currentPlaylistRef.current = null;
      currentPlaylistIndexRef.current = 0;
      
    } catch (error) {
      console.error('[Playlists] Error in handleStop:', error);
    }
  };
  
  /**
   * PLAY/PAUSE handler - Uses soundscapeService pause/resume
   */
  const handlePlayPause = async () => {
    if (!playingSound) return;
    
    try {
      if (isPlaying) {
        soundscapeService.pause();
        therapyTimer.pause();
        setIsPlaying(false);
      } else {
        const resumed = soundscapeService.resume();
        if (resumed) {
          therapyTimer.resume();
          setIsPlaying(true);
        } else {
          await startPlayback(playingSound, currentPlaylist, currentPlaylistIndex);
        }
      }
    } catch (error) {
      console.error('[Playlists] PlayPause error:', error);
    }
  };
  
  /**
   * NEXT handler (manual) - Crossfade to next track
   */
  const handleNext = () => {
    const playlist = currentPlaylistRef.current;
    const idx = currentPlaylistIndexRef.current;
    if (!playlist || !playlist.sounds || playlist.sounds.length === 0) return;
    
    // Cancel any scheduled crossfade
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }
    
    const next = getNextTrackInfo();
    if (!next) return;
    
    currentPlaylistIndexRef.current = next.index;
    
    // Use crossfade if currently playing, otherwise start fresh
    if (soundscapeService.getIsPlaying()) {
      const trackForService = buildTrackForService(next.sound);
      soundscapeService.crossfadeTo(trackForService, {
        volume: volume / 100,
        onEnded: handleTrackEnded
      }).then(result => {
        if (result.success) {
          setPlayingSound(next.sound);
          setCurrentPlaylistIndex(next.index);
          preloadNextTrack();
          scheduleCrossfade();
        }
      });
    } else {
      startPlayback(next.sound, playlist, next.index);
    }
  };
  
  /**
   * Play a playlist from the beginning
   */
  const playPlaylist = (playlist) => {
    if (!playlist.sounds || playlist.sounds.length === 0) {
      setError('Cette playlist est vide');
      return;
    }
    
    // DIAGNOSTIC: T0 = click timestamp
    window.__playlistDiag = { t0: performance.now(), steps: [] };
    window.__playlistDiag.steps.push({ step: '1_click_handler', ts: performance.now() });
    
    setLoadingPlaylistId(playlist.id); // V2.2.5: Show loading on button
    const firstSound = playlist.sounds[0];
    
    window.__playlistDiag.steps.push({ step: '2_track_resolved', ts: performance.now(), track: firstSound.name || firstSound.display_name });
    
    startPlayback(firstSound, playlist, 0);
  };
  
  /**
   * Quick preview - plays a short excerpt (10s) without opening UnifiedPlayer
   * Uses a dedicated HTMLAudioElement independent from the main player
   */
  const togglePreview = (e, sound) => {
    e.stopPropagation();
    
    // Debug: Log sound object
    console.log('🎵 [Playlists] togglePreview called with sound:', sound);
    console.log('🎵 [Playlists] REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
    
    // If this sound is already being previewed, stop it
    if (previewingSound && previewingSound.path === sound.path) {
      stopPreview();
      return;
    }
    
    // Stop any existing preview
    stopPreview();
    
    // Build audio URL
    let audioUrl = sound.path;
    if (!sound.path.startsWith('http')) {
      if (sound.path.startsWith('/api/')) {
        audioUrl = `${process.env.REACT_APP_BACKEND_URL}${extToM4a(sound.path)}`;
      } else if (sound.path.startsWith('/uploads/')) {
        audioUrl = `${process.env.REACT_APP_BACKEND_URL}${sound.path}`;
      } else if (sound.path.startsWith('/')) {
        // Other absolute paths - ensure .m4a extension
        audioUrl = `${process.env.REACT_APP_BACKEND_URL}${extToM4a(sound.path)}`;
      } else {
        // Relative path like "Classical/Classical_022.m4a" - build R2 stream URL
        // Handle both "Category/filename.ext" and just "filename.ext"
        const parts = sound.path.split('/');
        if (parts.length === 2) {
          const [category, filename] = parts;
          // V1.9: Use buildAudioUrl for proper encoding
          audioUrl = buildAudioUrl(category, extToM4a(filename));
        } else {
          // Just filename - use sound.category if available
          const category = sound.category || 'classical';
          // V1.9: Use buildAudioUrl for proper encoding
          audioUrl = buildAudioUrl(category, extToM4a(sound.path));
        }
      }
    }
    
    console.log('🎵 [Playlists] Preview URL:', audioUrl);
    
    // Create dedicated preview audio element
    const previewAudio = new Audio(audioUrl);
    previewAudio.volume = 0.7;
    previewAudioRef.current = previewAudio;
    
    // Set previewing state
    setPreviewingSound(sound);
    
    // Play preview
    previewAudio.play().catch(err => {
      console.error('❌ [Playlists] Preview playback error:', err);
      stopPreview();
    });
    
    // Auto-stop after PREVIEW_DURATION_MS
    previewTimeoutRef.current = setTimeout(() => {
      stopPreview();
    }, PREVIEW_DURATION_MS);
    
    // Stop when audio ends (if shorter than preview duration)
    previewAudio.onended = () => {
      stopPreview();
    };
  };
  
  /**
   * Stop preview playback
   */
  const stopPreview = () => {
    // Clear timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
    
    // Stop and cleanup audio element
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.src = '';
      previewAudioRef.current = null;
    }
    
    // Clear state
    setPreviewingSound(null);
  };
  
  // Cleanup preview on unmount
  useEffect(() => {
    return () => {
      stopPreview();
    };
  }, []);

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) {
      setError('Le nom de la playlist ne peut pas être vide');
      return;
    }

    try {
      const newPlaylist = await api.createPlaylist(newPlaylistName, []);
      
      setPlaylists([...playlists, newPlaylist]);
      setShowCreateModal(false);
      setNewPlaylistName('');
      const successMsg = t?.locale === 'fr-FR' 
        ? 'Playlist créée avec succès !'
        : 'Playlist created successfully!';
      setSuccess(successMsg);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating playlist:', err);
      // V1.1: Handle limit reached with soft message
      if (err.type === 'LIMIT_REACHED') {
        setShowCreateModal(false);
        setNewPlaylistName('');
        setLimitMessage(err.message);
        setTimeout(() => setLimitMessage(null), 5000);
        return;
      }
      const errorMsg = t?.locale === 'fr-FR' 
        ? 'Erreur lors de la création de la playlist'
        : 'Error creating playlist';
      setError(errorMsg);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeletePlaylistClick = (playlistId, playlistName) => {
    setDeleteConfirmModal({ show: true, playlistId, playlistName });
  };

  const handleDeletePlaylistConfirm = async () => {
    const { playlistId } = deleteConfirmModal;
    
    // Close modal first
    setDeleteConfirmModal({ show: false, playlistId: null, playlistName: '' });

    try {
      await api.deletePlaylist(playlistId);
      setPlaylists(playlists.filter(p => p.id !== playlistId));
      const successMsg = t?.locale === 'fr-FR' 
        ? 'Playlist supprimée'
        : 'Playlist deleted';
      setSuccess(successMsg);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting playlist:', err);
      const errorMsg = t?.locale === 'fr-FR' 
        ? 'Erreur lors de la suppression'
        : 'Error deleting playlist';
      setError(errorMsg);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDeletePlaylistCancel = () => {
    setDeleteConfirmModal({ show: false, playlistId: null, playlistName: '' });
  };

  const handleToggleSoundInPlaylist = async (sound) => {
    if (!editingPlaylist) return;

    // Check if sound is already in playlist
    const isAlreadyInPlaylist = editingPlaylist.sounds?.some(s => s.path === sound.path);
    
    let updatedSounds;
    let successMsg;
    
    if (isAlreadyInPlaylist) {
      // Remove sound from playlist
      updatedSounds = editingPlaylist.sounds.filter(s => s.path !== sound.path);
      successMsg = t?.locale === 'fr-FR' 
        ? `${sound.display_name || sound.filename} retiré de la playlist`
        : `${sound.display_name || sound.filename} removed from playlist`;
    } else {
      // ⭐ FREE USER LIMIT CHECK
      const currentSoundCount = editingPlaylist.sounds?.length || 0;
      if (isFreeUser && currentSoundCount >= 3) {
        // Open premium modal instead of adding
        setShowPremiumModal(true);
        setError(
          t?.locale === 'fr-FR'
            ? '🔒 Limite atteinte — débloquez les playlists illimitées avec Hearen Premium.'
            : '🔒 Limit reached — unlock unlimited playlists with Hearen Premium.'
        );
        setTimeout(() => setError(''), 4000);
        return;
      }
      
      // Add sound to playlist
      const soundRef = {
        path: sound.path,
        name: sound.display_name || sound.filename,
        type: 'library',
        category: selectedCategory
      };
      updatedSounds = [...editingPlaylist.sounds, soundRef];
      successMsg = t?.locale === 'fr-FR' 
        ? `${sound.display_name || sound.filename} ajouté à la playlist`
        : `${sound.display_name || sound.filename} added to playlist`;
    }
    
    try {
      await api.updatePlaylist(editingPlaylist.id, {
        sounds: updatedSounds
      });
      
      setEditingPlaylist({ ...editingPlaylist, sounds: updatedSounds });
      setPlaylists(playlists.map(p => 
        p.id === editingPlaylist.id ? { ...p, sounds: updatedSounds } : p
      ));
      
      // Show success message
      setSuccess(successMsg);
      setTimeout(() => setSuccess(''), 2000);
      
      // Visual feedback
      setRecentlyAddedSound(sound.path);
      setTimeout(() => setRecentlyAddedSound(null), 1500);
    } catch (err) {
      console.error('Error toggling sound:', err);
      const errorMsg = t?.locale === 'fr-FR' 
        ? 'Erreur lors de la modification'
        : 'Error updating playlist';
      setError(errorMsg);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRemoveSoundFromPlaylist = async (playlistId, soundIndex) => {
    const playlist = playlists.find(p => p.id === playlistId);
    if (!playlist) return;

    const updatedSounds = playlist.sounds.filter((_, idx) => idx !== soundIndex);
    
    try {
      await api.updatePlaylist(playlistId, {
        sounds: updatedSounds
      });
      
      setPlaylists(playlists.map(p => 
        p.id === playlistId ? { ...p, sounds: updatedSounds } : p
      ));
      
      if (editingPlaylist?.id === playlistId) {
        setEditingPlaylist({ ...editingPlaylist, sounds: updatedSounds });
      }
    } catch (err) {
      console.error('Error removing sound:', err);
      const errorMsg = t?.locale === 'fr-FR' 
        ? 'Erreur lors de la suppression du son'
        : 'Error removing sound';
      setError(errorMsg);
      setTimeout(() => setError(''), 3000);
    }
  };

  const handlePlayPlaylist = (playlist) => {
    // Use startTransition for smoother navigation without flash
    startTransition(() => {
      navigate(`/therapy?playlist=${playlist.id}`, { replace: true });
    });
  };
  
  // V1 Unified Player: Play a specific sound from playlist (with crossfade)
  const handlePlaySound = async (sound) => {
    if (!sound) return;
    
    setPlayingSound(sound);
    setPlayerActive(true);
    
    // Find index in current playlist
    if (currentPlaylist) {
      const idx = currentPlaylist.sounds.findIndex(s => s.path === sound.path);
      if (idx >= 0) {
        setCurrentPlaylistIndex(idx);
        currentPlaylistIndexRef.current = idx;
      }
    }
    
    // Cancel any scheduled crossfade
    if (crossfadeTimerRef.current) {
      clearTimeout(crossfadeTimerRef.current);
      crossfadeTimerRef.current = null;
    }
    
    const trackForService = buildTrackForService(sound);
    
    try {
      if (soundscapeService.getIsPlaying()) {
        // Crossfade to the new track
        const result = await soundscapeService.crossfadeTo(trackForService, {
          volume: volume / 100 || 0.80,
          onEnded: handleTrackEnded
        });
        
        if (result.success) {
          setIsPlaying(true);
          preloadNextTrack();
          scheduleCrossfade();
          if (timerActive) therapyTimer.resume();
        }
      } else {
        // Not playing, start fresh
        await startPlayback(sound, currentPlaylist, currentPlaylistIndexRef.current);
      }
    } catch (error) {
      console.error('[Playlists] handlePlaySound error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] pt-20 px-8 pb-8 flex items-center justify-center">
        <p className="text-white/60">Chargement...</p>
      </div>
    );
  }

  // Fullscreen mode when playing (like Radio and TherapyPlayer)

  // Fullscreen mode when in player view - V1 UNIFIED PLAYER
  // Show UnifiedPlayer when playerActive AND a sound is selected (not just when playing)
  if (playerActive && playingSound) {
    // Get playlist background or default
    const playerBgImage = 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=1920&q=80';
    
    // Current track info
    const currentTrackInfo = playingSound ? {
      display_name: playingSound.display_name || playingSound.name,
      display_name_fr: playingSound.translations?.fr || playingSound.display_name || playingSound.name,
      display_name_en: playingSound.translations?.en || playingSound.display_name || playingSound.name,
      filename: playingSound.file || playingSound.path,
      url: playingSound.url
    } : null;
    
    // Audio profile info (using equalizer settings)
    // Always show AUDIO_BANDS_COUNT frequencies (total tested) even if fewer have active compensation
    const hasAudioProfile = !!(equalizerSettings?.has_audiometric_compensation);
    const audioProfileInfo = hasAudioProfile ? {
      nbFrequences: AUDIO_BANDS_COUNT, // Standard audiometric frequencies (125-8000 Hz)
      type: 'equalizer'
    } : null;
    
    // Tinnitus info from userSettings if available
    // V1.1 FIX: Check notchEnabled (current toggle state) not just labProgram.enabled
    const hasTinnitusTest = !!(labProgram?.enabled);
    const isTinnitusActive = notchEnabled && hasTinnitusTest;
    // BUILD 52.5: Quantize gain to 0.5 dB for consistent display
    const rawGain = labProgram?.gain_db;
    const tinnitusInfo = hasTinnitusTest ? {
      frequency: labProgram?.f_t || 8000,
      gain: rawGain !== null && rawGain !== undefined ? quantizeDb(rawGain) : null,  // BUILD 52.5: Quantized
      type: labProgram?.program_type === 'notch' ? 'notch' : 'inverse',
      active: isTinnitusActive // V1.1: Include active state
    } : null;
    
    // Check if user is Free
    const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';
    
    // V1.1: currentLang now comes from useLanguage hook (reactive)
    
    // Current index in playlist
    const currentIndex = currentPlaylist ? 
      currentPlaylist.sounds.findIndex(s => s.path === playingSound.path) : -1;
    
    // Handle previous track
    const handlePrevTrack = () => {
      if (!currentPlaylist || currentIndex <= 0) return;
      const prevSound = currentPlaylist.sounds[currentIndex - 1];
      handlePlaySound(prevSound);
    };
    
    // Handle next track
    const handleNextTrack = () => {
      if (!currentPlaylist || currentIndex >= currentPlaylist.sounds.length - 1) return;
      const nextSound = currentPlaylist.sounds[currentIndex + 1];
      handlePlaySound(nextSound);
    };
    
    return (
      <>
      <UnifiedPlayer
        // Context
        context="playlist"
        contextIcon="🎵"
        contextName={currentPlaylist?.name || t?.playlists || 'Playlist'}
        
        // Current track
        currentTrack={currentTrackInfo}
        
        // Playback state
        isPlaying={isPlaying}
        isLoading={false}
        currentTime={therapyTimer.getElapsedTime?.() || 0}
        duration={playingSound?.duration || 0}
        
        // Callbacks
        onBack={() => handleStop()}
        onPlayPause={handlePlayPause}
        onStop={handleStop}
        onPrev={handlePrevTrack}
        onNext={handleNextTrack}
        onSeek={null}
        onFavoriteToggle={null}
        onMenuClick={null} // FIX 2: Remove menu button, keep only favorite star
        
        // Favorite state
        isFavorite={false}
        
        // Navigation controls - Playlist has prev/next
        hasPrev={currentPlaylist && currentIndex > 0}
        hasNext={currentPlaylist && currentIndex < currentPlaylist.sounds.length - 1}
        showPrevNext={true}
        
        // Volume - sync UI state with actual audio volume
        volume={volume}
        onVolumeChange={(newVolume) => {
          setVolume(newVolume);
          soundscapeService.setVolume(newVolume / 100);
        }}
        
        // Acouphènes settings
        tinnitusEnabled={notchEnabled}
        onTinnitusToggle={() => {
          if (isFreeUser) {
            setShowPremiumModal(true);
          } else {
            setNotchEnabled(!notchEnabled);
          }
        }}
        tinnitusInfo={tinnitusInfo}
        tinnitusLocked={isFreeUser}
        hasTinnitusData={hasTinnitusTest}
        
        // Profil audio settings
        audioProfileEnabled={equalizerEnabled}
        onAudioProfileToggle={() => {
          if (isFreeUser) {
            setShowPremiumModal(true);
          } else {
            setEqualizerEnabled(!equalizerEnabled);
          }
        }}
        audioProfileInfo={audioProfileInfo}
        audioProfileLocked={isFreeUser}
        hasAudioProfileData={hasAudioProfile}
        onAudioProfileClick={() => setShowSpectrumPanel(true)} // FIX 1: Open spectrum modal (same as Radio)
        
        // Premium modal
        onShowPremiumModal={() => setShowPremiumModal(true)}
        
        // Language
        lang={currentLang}
        
        // Background
        backgroundImage={playerBgImage}
        
        // Translations
        t={{
          noTrackSelected: t?.noTrackSelected || 'Aucun son sélectionné',
          back: t?.back || 'Retour',
          favorite: t?.favorite || 'Favori',
          play: t?.play || 'Lecture',
          pause: t?.pause || 'Pause',
          stop: t?.stop || 'Arrêter',
          previous: t?.previous || 'Précédent',
          next: t?.next || 'Suivant',
          tinnitusLabel: t?.tinnitusLabel || 'Acouphènes',
          audioProfileLabel: t?.audioProfileLabel || 'Profil audio',
          frequencies: t?.frequencies || 'fréquences',
          premiumFeature: t?.premiumFeature || 'Fonctionnalité Premium'
        }}
      />
      
      {/* FIX 1 & 2: Audio Profile Modal (same as Radio) */}
      <AudioProfileModal
        isOpen={showSpectrumPanel}
        onClose={() => setShowSpectrumPanel(false)}
        audioService={audioService}
        isPlaying={isPlaying}
        equalizerSettings={equalizerSettings}
        equalizerEnabled={equalizerEnabled}
        setEqualizerEnabled={setEqualizerEnabled}
        notchEnabled={notchEnabled}
        setNotchEnabled={setNotchEnabled}
        labProgram={labProgram}
        onPlayPause={handlePlayPause}
        t={t}
        profileConfigured={hasAudioProfile}
        frequencyCount={audioProfileInfo?.nbFrequences || 0}
        onApplySettings={({ equalizerEnabled: eqEnabled, notchEnabled: nEnabled }) => {
          if (equalizerSettings) {
            audioService.loadEqualizer(equalizerSettings, eqEnabled, nEnabled);
          }
          saveAudioToggles(eqEnabled, nEnabled);
        }}
      />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1C] pt-20 px-4 md:px-8 pb-24 relative overflow-hidden">
      {/* V1.1: Soft limit message banner */}
      {limitMessage && (
        <div 
          className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50"
          style={{ animation: 'fadeSlideDown 300ms ease-out' }}
        >
          <div className="bg-gradient-to-r from-purple-600/90 to-violet-600/90 backdrop-blur-md px-6 py-3 rounded-xl border border-white/20 shadow-lg flex items-center gap-3 max-w-sm">
            <svg className="w-5 h-5 text-white/90 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white text-sm font-medium">{limitMessage}</span>
            <button 
              onClick={() => setLimitMessage(null)}
              className="text-white/70 hover:text-white ml-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Halo premium top-left */}
      <div 
        className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] pointer-events-none"
      />
      
      {/* Halo premium bottom-right */}
      <div 
        className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] pointer-events-none"
      />

      {/* Grain texture ultra léger */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Card Premium */}
        <div 
          className="mb-6 relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] px-5 py-4"
          style={{
            animation: 'fadeSlideUp 250ms ease-out'
          }}
        >
          {/* Overlay dégradé */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#3B48EE]/12 via-transparent to-[#6F42EA]/8 pointer-events-none" />
          
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              {/* Icône dans carré glass */}
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-2 border border-white/15 shadow-md flex items-center justify-center">
                <ListMusic className="h-6 w-6 text-white/80" />
              </div>
              
              <div>
                <h1 className="text-white font-semibold tracking-wide text-lg drop-shadow-[0_4px_14px_rgba(0,0,0,0.65)]">
                  {t?.playlistsTitle || 'Mes Playlists'}
                </h1>
                <p className="text-white/70 text-sm">
                  {t?.playlistsDesc || 'Créez et gérez vos playlists personnalisées'}
                </p>
              </div>
            </div>
            
            {/* Bouton New Playlist premium */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] text-white font-medium text-sm shadow-[0_0_24px_rgba(90,90,255,0.55)] hover:brightness-110 active:scale-[0.97] transition-all duration-200"
            >
              <Plus className="h-4 w-4" />
              {t?.newPlaylist || 'Nouvelle Playlist'}
            </button>
          </div>
        </div>

        {/* Alerts Premium */}
        {error && (
          <div className="mb-4 bg-red-500/20 backdrop-blur-lg border border-red-500/30 rounded-2xl p-4 flex items-center gap-3 shadow-lg">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <span className="text-red-200 text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-500/20 backdrop-blur-lg border border-green-500/30 rounded-2xl p-4 flex items-center gap-3 shadow-lg">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span className="text-green-200 text-sm">{success}</span>
          </div>
        )}

        {/* Playlists Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((playlist, playlistIndex) => (
            <div 
              key={playlist.id} 
              className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-5 hover:scale-[1.02] transition-transform duration-200"
              style={{
                animation: `fadeSlideUp 200ms ease-out ${playlistIndex * 50}ms both`
              }}
            >
              {/* Overlay dégradé */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
              
              <div className="relative space-y-4">
                {/* Header de la carte */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold tracking-wide truncate">
                      {playlist.name}
                    </h3>
                    <p className="text-white/60 text-sm">
                      {playlist.sounds?.length || 0} {t?.soundCount || 'son(s)'}
                    </p>
                  </div>
                  {/* FIX 2: Removed redundant music icon - ContentBadge already shows type */}
                </div>

                {/* Liste des sons */}
                {/* Liste des sons premium */}
                {playlist.sounds && playlist.sounds.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {playlist.sounds.map((sound, idx) => {
                      const isPlaying = playingSound === sound.path;
                      
                      return (
                        <div
                          key={idx}
                          className="relative overflow-hidden rounded-2xl bg-white/8 backdrop-blur-[6px] border border-white/10 shadow-[0_6px_20px_rgba(0,0,0,0.55)] px-4 py-2.5 flex items-center justify-between gap-3"
                        >
                          {/* Index du morceau */}
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border border-white/10 flex items-center justify-center">
                            <span className="text-[11px] text-white/50 font-medium">
                            {idx + 1}
                            </span>
                          </div>
                          
                          {/* Nom du son */}
                          <span className="truncate flex-1 text-white/90 text-sm leading-snug">{sound.name}</span>
                          
                          {/* Bouton poubelle adouci */}
                          <button
                            onClick={() => handleRemoveSoundFromPlaylist(playlist.id, idx)}
                            className="flex-shrink-0 text-red-400/80 hover:text-red-300 bg-white/5 rounded-full p-1.5 transition-all duration-150"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Boutons d'action premium */}
                <div className="flex items-center gap-3 pt-2">
                  {/* Bouton Listen - principal gradient */}
                  <button
                    onClick={() => playPlaylist(playlist)}
                    disabled={!playlist.sounds || playlist.sounds.length === 0 || loadingPlaylistId === playlist.id}
                    className={`flex-1 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-white font-medium text-sm transition-all duration-200 ${
                      !playlist.sounds || playlist.sounds.length === 0
                        ? 'bg-white/5 border border-white/10 text-white/40 cursor-not-allowed'
                        : loadingPlaylistId === playlist.id
                        ? 'bg-gradient-to-br from-[#3B48EE]/70 to-[#6F42EA]/70 cursor-wait'
                        : 'bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] shadow-[0_0_24px_rgba(90,90,255,0.55)] hover:brightness-110 active:scale-[0.97]'
                    }`}
                    data-testid="playlist-listen-btn"
                  >
                    {loadingPlaylistId === playlist.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Play className="h-3 w-3" />
                    )}
                    {loadingPlaylistId === playlist.id ? 'Chargement...' : 'TEST PLAYLIST'}
                  </button>
                  
                  {/* Bouton Add - secondaire glass */}
                  <button
                    onClick={() => {
                      setEditingPlaylist(playlist);
                      setShowAddSoundsModal(true);
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 bg-white/5 backdrop-blur-md border border-white/15 text-white/85 font-medium text-sm hover:bg-white/10 active:scale-[0.97] transition-all duration-200"
                  >
                    <Plus className="h-3 w-3" />
                    {t?.addSound || 'Ajouter'}
                  </button>
                  
                  {/* Bouton poubelle globale - destructive adouci */}
                  <button
                    onClick={() => handleDeletePlaylistClick(playlist.id, playlist.name)}
                    className="text-red-400/80 bg-white/5 rounded-full p-2 hover:bg-red-500/20 hover:text-red-200 transition-all duration-150"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {playlists.length === 0 && (
          <div className="relative overflow-hidden rounded-3xl bg-white/5 backdrop-blur-[6px] border border-white/10 shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-12 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/8 via-transparent to-[#6F42EA]/6 pointer-events-none" />
            
            <div className="relative">
              <ListMusic className="h-16 w-16 mx-auto mb-4 text-white/30" />
              <p className="text-white/60 mb-6">{t?.noPlaylistsMessage || 'Aucune playlist. Créez votre première playlist !'}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] text-white font-medium text-sm shadow-[0_0_24px_rgba(90,90,255,0.55)] hover:brightness-110 active:scale-[0.97] transition-all duration-200"
              >
                <Plus className="h-4 w-4" />
                {t?.createPlaylist || 'Créer une Playlist'}
              </button>
            </div>
          </div>
        )}

        {/* Create Playlist Modal Premium */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div 
              className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] p-6 relative overflow-hidden"
              style={{
                animation: 'fadeSlideUp 250ms ease-out'
              }}
            >
              {/* Overlay dégradé */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/15 via-transparent to-[#3B48EE]/10 pointer-events-none" />
              
              <div className="relative space-y-4">
                <div>
                  <h2 className="text-white font-semibold text-xl mb-2 tracking-wide">
                    {t?.createPlaylist || 'Créer une Playlist'}
                  </h2>
                  <p className="text-white/70 text-sm">
                    {t?.locale === 'fr-FR' 
                      ? 'Donnez un nom à votre playlist' 
                      : 'Give your playlist a name'}
                  </p>
                </div>
                
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  placeholder={t?.locale === 'fr-FR' ? 'Ex: Détente du soir' : 'Ex: Evening Relaxation'}
                  className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#6F42EA] focus:border-transparent"
                  autoFocus
                  onKeyPress={(e) => e.key === 'Enter' && handleCreatePlaylist()}
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setNewPlaylistName('');
                    }}
                    className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white backdrop-blur-md rounded-2xl font-medium text-sm transition-all duration-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreatePlaylist}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] text-white rounded-2xl font-medium text-sm shadow-[0_0_24px_rgba(90,90,255,0.55)] hover:brightness-110 active:scale-[0.97] transition-all duration-200"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Sounds Modal Premium */}
        {showAddSoundsModal && editingPlaylist && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-20 pb-28 animate-in fade-in duration-200">
            <div className="w-full max-w-3xl flex flex-col h-full max-h-[80vh] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
              
              {/* Header + Categories - Ultra Compact & Discrete */}
              <div className="shrink-0 px-6 pt-2 pb-1.5 space-y-1.5 border-b border-white/10">
                {/* Header Title */}
                <div>
                  <h2 className="text-white font-semibold text-base leading-tight">
                    {t?.locale === 'fr-FR' 
                      ? `Ajouter des sons à "${editingPlaylist.name}"` 
                      : `Add sounds to "${editingPlaylist.name}"`}
                  </h2>
                  <p className="text-white/60 text-[10px] mt-1 leading-tight">
                    {t?.locale === 'fr-FR' 
                      ? 'Sélectionnez une catégorie puis des sons' 
                      : 'Select a category then sounds'}
                  </p>
                </div>
                
                {/* ⭐ FREE USER BANNER - Ultra Compact */}
                {isFreeUser && (
                  <div className="bg-gradient-to-r from-[#6F42EA]/20 to-[#8B5CF6]/20 border border-[#6F42EA]/30 rounded-lg p-2 backdrop-blur-md">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <Star className="w-3.5 h-3.5 text-[#6F42EA] flex-shrink-0" />
                        <span className="text-[10px] text-white/90 leading-tight truncate">
                          {t?.locale === 'fr-FR' 
                            ? 'Free: 3 sons max - Premium: illimité'
                            : 'Free: 3 sounds max - Premium: unlimited'}
                        </span>
                      </div>
                      <button
                        onClick={() => setShowPremiumModal(true)}
                        className="bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] hover:from-[#5F32DA] hover:to-[#7B4CE6] text-white font-semibold py-1 px-2.5 rounded-lg transition-all shadow-[0_2px_10px_rgba(111,66,234,0.4)] hover:shadow-[0_3px_15px_rgba(111,66,234,0.6)] flex items-center gap-1 flex-shrink-0"
                      >
                        <Crown className="w-3 h-3" />
                        <span className="text-[10px]">
                          {t?.locale === 'fr-FR' ? 'Premium' : 'Upgrade'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Success message in modal */}
                {success && (
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-2 flex items-center gap-2 animate-in fade-in">
                    <CheckCircle className="h-3.5 w-3.5 text-green-400 flex-shrink-0" />
                    <span className="text-xs text-green-200 font-medium">{success}</span>
                  </div>
                )}

                {/* Category Selector - Discrete & Secondary */}
                <div className="grid grid-cols-4 gap-1.5">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-2 py-1.5 rounded-lg border transition-all backdrop-blur-md ${
                        selectedCategory === cat.id
                          ? 'border-[#6F42EA] bg-[#6F42EA]/20 shadow-[0_0_12px_rgba(111,66,234,0.3)]'
                          : 'border-white/10 bg-white/3 hover:border-[#6F42EA]/40 hover:bg-white/8'
                      }`}
                    >
                      <div className="text-base leading-none">{cat.icon}</div>
                      <div className="text-[9px] font-medium truncate text-white/80 mt-0.5 leading-tight">{cat.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sounds List - Scrollable area that takes all remaining space */}
              <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-2">
                {/* BUILD 56.1: Loading state */}
                {loadingSounds && (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <div className="w-8 h-8 border-2 border-[#6F42EA] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-white/60">
                      {t?.locale === 'fr-FR' ? 'Chargement des sons...' : 'Loading sounds...'}
                    </span>
                  </div>
                )}
                
                {/* BUILD 56.1: Error state with retry */}
                {!loadingSounds && loadingSoundsError && (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <div className="text-red-400 text-sm text-center">
                      {t?.locale === 'fr-FR' ? 'Erreur de chargement' : 'Loading error'}
                    </div>
                    <div className="text-white/40 text-xs text-center">{loadingSoundsError}</div>
                    <button
                      onClick={() => loadAvailableSounds()}
                      className="mt-2 px-4 py-2 bg-[#6F42EA]/20 border border-[#6F42EA]/40 rounded-lg text-sm text-white hover:bg-[#6F42EA]/30 transition-colors"
                    >
                      {t?.locale === 'fr-FR' ? 'Réessayer' : 'Retry'}
                    </button>
                  </div>
                )}
                
                {/* BUILD 56.1: Empty state */}
                {!loadingSounds && !loadingSoundsError && (!availableSounds[selectedCategory] || availableSounds[selectedCategory].length === 0) && (
                  <div className="flex flex-col items-center justify-center py-12 gap-2">
                    <Music className="w-8 h-8 text-white/30" />
                    <span className="text-sm text-white/50">
                      {t?.locale === 'fr-FR' ? 'Aucun son disponible dans cette catégorie' : 'No sounds available in this category'}
                    </span>
                  </div>
                )}
                
                {/* Sounds list */}
                {!loadingSounds && !loadingSoundsError && availableSounds[selectedCategory]?.map((sound, idx) => {
                    const wasJustToggled = recentlyAddedSound === sound.path;
                    const isInPlaylist = editingPlaylist?.sounds?.some(s => s.path === sound.path);
                    const isPlaying = playingSound === sound.path;
                    
                    // ⭐ FREE USER LOCK CHECK
                    const currentSoundCount = editingPlaylist?.sounds?.length || 0;
                    const isLockedForFree = isFreeUser && !isInPlaylist && currentSoundCount >= 3;
                    
                    return (
                      <div
                        key={idx}
                        onClick={() => isLockedForFree ? setShowPremiumModal(true) : handleToggleSoundInPlaylist(sound)}
                        className={`p-3 border-b border-white/10 transition-all duration-200 ${
                          isLockedForFree 
                            ? 'cursor-not-allowed opacity-50 hover:bg-white/5' 
                            : 'cursor-pointer ' + (isInPlaylist 
                              ? 'bg-[#6F42EA]/20 hover:bg-[#6F42EA]/30' 
                              : 'hover:bg-white/5')
                        } ${wasJustToggled ? 'ring-2 ring-[#6F42EA]' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                      {/* Track index */}
                      <div className="flex-shrink-0 w-9 h-9 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                        <span className="text-sm text-white/50 font-medium">
                          {idx + 1}
                          </span>
                      </div>
                          
                          <span className={`text-base flex-1 ${isInPlaylist ? 'font-semibold text-white' : 'text-white/80 font-medium'}`}>
                            {sound.display_name || sound.filename}
                          </span>
                          
                          {/* ⭐ LOCK ICON FOR FREE USERS */}
                          {isLockedForFree && (
                            <Lock className="h-4 w-4 text-white/50 flex-shrink-0" />
                          )}
                          
                          {/* Favorite Star Button */}
                          {!isLockedForFree && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFavorite(sound);
                              }}
                              className={`flex-shrink-0 p-2 -m-1 rounded-full transition-all z-10 ${
                                isFavorite(sound.path)
                                  ? 'text-yellow-400 hover:text-yellow-300'
                                  : 'text-white/40 hover:text-white/70 hover:bg-white/10'
                              }`}
                              title={isFavorite(sound.path) ? (t?.locale === 'fr-FR' ? 'Retirer des favoris' : 'Remove from favorites') : (t?.locale === 'fr-FR' ? 'Ajouter aux favoris' : 'Add to favorites')}
                              data-testid={`favorite-star-${idx}`}
                            >
                              <Star className={`h-5 w-5 ${isFavorite(sound.path) ? 'fill-current' : ''}`} />
                            </button>
                          )}
                          
                          {isInPlaylist && !isLockedForFree && (
                            <CheckCircle className="h-5 w-5 text-[#6F42EA] flex-shrink-0" />
                          )}
                        </div>
                        {isInPlaylist && (
                          <div className="text-[10px] text-[#6F42EA] mt-1 ml-10">
                            {t?.locale === 'fr-FR' ? '✓ Dans la playlist - Cliquer pour retirer' : '✓ In playlist - Click to remove'}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
              
              {/* Footer - Compact */}
              <div className="shrink-0 px-6 pt-2 pb-3 border-t border-white/10">
                <button
                  onClick={() => {
                    setShowAddSoundsModal(false);
                    setEditingPlaylist(null);
                  }}
                  className="w-full px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white backdrop-blur-md rounded-2xl font-medium text-sm transition-all duration-200"
                >
                  {t?.close || 'Fermer'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Delete Confirmation Modal Premium */}
        {deleteConfirmModal.show && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200"
            onClick={handleDeletePlaylistCancel}
          >
            <div 
              className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 max-w-md w-full mx-4 shadow-[0_20px_60px_rgba(0,0,0,0.6)] relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: 'fadeSlideUp 250ms ease-out'
              }}
            >
              {/* Overlay dégradé */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-transparent pointer-events-none" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-red-500/20 p-2 rounded-xl backdrop-blur-md border border-red-500/30">
                    <Trash2 className="h-6 w-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">
                    {t?.confirmDeleteTitle || 'Confirmer la suppression'}
                  </h3>
                </div>
                
                <p className="text-white/70 mb-6">
                  {t?.confirmDeleteMessage || 'Êtes-vous sûr de vouloir supprimer définitivement'}{' '}
                  <span className="font-semibold text-white">&quot;{deleteConfirmModal.playlistName}&quot;</span> ?
                </p>
                
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleDeletePlaylistCancel}
                    className="px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white backdrop-blur-md rounded-2xl font-medium text-sm transition-all duration-200"
                  >
                    {t?.cancel || 'Annuler'}
                  </button>
                  <button
                    onClick={handleDeletePlaylistConfirm}
                    className="px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-medium text-sm shadow-lg flex items-center gap-2 transition-all duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t?.delete || 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ⭐ PREMIUM UPSELL MODAL */}
        {showPremiumModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-6 animate-in fade-in duration-200">
            <div className="w-full max-w-2xl bg-gradient-to-br from-[#1a1f2e] to-[#252b3d] border-2 border-[#6F42EA]/30 rounded-3xl shadow-[0_30px_100px_rgba(111,66,234,0.7)] overflow-hidden animate-in zoom-in-95 duration-300">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] p-8 pb-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="w-10 h-10 text-white" />
                    <div>
                      <h2 className="text-3xl font-bold text-white">Hearen Premium</h2>
                      <p className="text-white/90 text-sm mt-1">
                        {t?.locale === 'fr-FR' ? 'Débloquez toutes les fonctionnalités' : 'Unlock all features'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPremiumModal(false)}
                    className="p-2 rounded-xl bg-white/20 hover:bg-white/30 transition-all"
                  >
                    <Plus className="w-6 h-6 text-white rotate-45" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                    <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {t?.locale === 'fr-FR' ? 'Playlists illimitées' : 'Unlimited Playlists'}
                      </h3>
                      <p className="text-white/70 text-sm mt-1">
                        {t?.locale === 'fr-FR' 
                          ? 'Créez autant de playlists que vous le souhaitez avec un nombre illimité de sons'
                          : 'Create as many playlists as you want with unlimited sounds'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                    <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {t?.locale === 'fr-FR' ? 'Bibliothèque complète' : 'Full Library Access'}
                      </h3>
                      <p className="text-white/70 text-sm mt-1">
                        {t?.locale === 'fr-FR' 
                          ? 'Accédez à tous les sons et soundscapes thérapeutiques'
                          : 'Access more soundscapes and listening environments'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                    <CheckCircle className="w-6 h-6 text-[#6F42EA] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {t?.locale === 'fr-FR' ? 'Personnalisation avancée' : 'Advanced Customization'}
                      </h3>
                      <p className="text-white/70 text-sm mt-1">
                        {t?.locale === 'fr-FR' 
                          ? 'Égaliseur, compensation audio, filtres personnalisés et bien plus'
                          : 'Equalizer, audio compensation, custom filters and more'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => {
                    setShowPremiumModal(false);
                    navigate('/premium');
                  }}
                  className="w-full bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] hover:from-[#5F32DA] hover:to-[#7B4CE6] text-white font-bold py-4 px-6 rounded-2xl transition-all shadow-[0_8px_30px_rgba(111,66,234,0.5)] hover:shadow-[0_12px_40px_rgba(111,66,234,0.7)] hover:scale-105 flex items-center justify-center gap-3"
                >
                  <Crown className="w-5 h-5" />
                  <span className="text-lg">
                    {t?.locale === 'fr-FR' ? 'Passer à Premium maintenant' : 'Upgrade to Premium Now'}
                  </span>
                </button>

                <p className="text-center text-white/50 text-xs">
                  {t?.locale === 'fr-FR' 
                    ? 'Vous serez redirigé vers les paramètres d\'abonnement'
                    : 'You will be redirected to subscription settings'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Unified Audio Controls removed - now in fullscreen player only */}
    </div>
  );
};

export default Playlists;
